import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export function formatApiError(error: any, fallback = '請求失敗') {
  const data = error?.info || error?.response?.data || error?.data;
  if (typeof data?.errorMessage === 'string' && data.errorMessage.trim()) {
    return data.errorMessage;
  }
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        const message = String(item?.msg || '').replace(/^Value error, /, '');
        const field = Array.isArray(item?.loc) ? String(item.loc[item.loc.length - 1] || '') : '';
        const labels: Record<string, string> = {
          email: '電郵',
          username: '電郵',
          password: '密碼',
          invite_code: '邀請碼',
          name: '姓名',
          phone: '電話',
        };
        const label = labels[field] || field;
        if (message && label) return `${label}：${message}`;
        return message;
      })
      .filter(Boolean);
    if (parts.length) return parts.join('；');
  }
  if (typeof error?.message === 'string' && error.message && error.message !== 'Error') {
    return error.message;
  }
  if (error?.response?.status) {
    return `請求失敗：${error.response.status}`;
  }
  return fallback;
}

function cookieValue(name: string) {
  const prefix = `${name}=`;
  const item = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : '';
}

enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

interface ResponseStructure {
  success?: boolean;
  data?: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

export const errorConfig: RequestConfig = {
  errorConfig: {
    errorThrower: (res) => {
      const payload = res as unknown as ResponseStructure;
      if (payload && typeof payload === 'object' && payload.success === false) {
        const error: any = new Error(payload.errorMessage);
        error.name = 'BizError';
        error.info = {
          errorCode: payload.errorCode,
          errorMessage: payload.errorMessage,
          showType: payload.showType,
          data: payload.data,
        };
        throw error;
      }
    },
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        message.error(formatApiError(error, `請求失敗：${error.response.status}`));
      } else if (error.request) {
        message.error('伺服器無響應，請確認後端已啓動');
      } else {
        message.error('請求發送失敗，請重試');
      }
    },
  },
  requestInterceptors: [
    (config: RequestOptions) => {
      localStorage.removeItem('sukchaak-token');
      const headers = { ...(config.headers || {}) };
      const method = (config.method || 'GET').toUpperCase();
      const csrf = cookieValue('csrf_token');
      if (!SAFE_METHODS.has(method) && csrf) {
        headers['X-CSRF-Token'] = csrf;
      }
      return { ...config, credentials: 'include', headers };
    },
  ],
  responseInterceptors: [
    (response) => {
      const { data } = response as unknown as ResponseStructure;
      if (data?.success === false) {
        message.error(data.errorMessage || '請求失敗');
      }
      return response;
    },
  ],
};
